import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Clock, TrendingUp, TrendingDown } from "lucide-react";

interface ProductivityMetricsProps {
  avgResolutionHours: number;
  resolutionRate: number;
  sameDay: number;
  oneToThreeDays: number;
  threeToSevenDays: number;
  moreThanSevenDays: number;
  backlogChange: number;
}

export const ProductivityMetrics = ({
  avgResolutionHours,
  resolutionRate,
  sameDay,
  oneToThreeDays,
  threeToSevenDays,
  moreThanSevenDays,
  backlogChange,
}: ProductivityMetricsProps) => {
  const distributionData = [
    { name: "< 1 dia", value: sameDay, color: "hsl(var(--chart-1))" },
    { name: "1-3 dias", value: oneToThreeDays, color: "hsl(var(--chart-2))" },
    { name: "3-7 dias", value: threeToSevenDays, color: "hsl(var(--chart-3))" },
    { name: "> 7 dias", value: moreThanSevenDays, color: "hsl(var(--chart-4))" },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Análise de Produtividade</h2>
      
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tempo Médio de Resolução</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgResolutionHours.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground mt-1">
              {(avgResolutionHours / 24).toFixed(1)} dias em média
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Resolução</CardTitle>
            {resolutionRate >= 80 ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{resolutionRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Tickets resolvidos no período
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Variação do Backlog</CardTitle>
            {backlogChange <= 0 ? (
              <TrendingDown className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingUp className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${backlogChange <= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {backlogChange > 0 ? '+' : ''}{backlogChange}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {backlogChange <= 0 ? 'Redução' : 'Aumento'} no período
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Distribuição de Tempo de Resolução</CardTitle>
          <CardDescription>Quantidade de tickets por tempo de resolução</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={distributionData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="name" 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {distributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
